import {
  MULTIPLIER_TIERS,
  daysServed,
  earnedXP,
  exitQuote,
  isMatured,
  multiplierForDays,
  roundXp,
  xpAtMaturity,
} from "../../config/supernovaXp";
import { SUPERNOVA_TOKENS } from "./supernovaContracts";
import { lockChipLabel, presetForDays } from "./supernovaStakePreview";

/**
 * supernovaExitQuote — the pure model behind the Active stakes table and the
 * unstake modal, the same way supernovaStakePreview backs the stake modal.
 *
 * No React, no formatting, no I/O. It takes the stake lots the chain read
 * returned and produces the figures each row shows, plus the line-by-line quote
 * an early exit would actually pay.
 *
 * EVERY NUMBER COMES FROM config/supernovaXp.js. `earnedXP`, `xpAtMaturity` and
 * `exitQuote` are the model; this file arranges what they return into rows and
 * lines and names the tier they rated against. It derives no XP of its own, so
 * the table, the modal and the engine cannot drift apart.
 *
 * WHAT AN EXIT QUOTE IS, AND IS NOT. It is a DISPLAY of the model at a moment
 * in time — the same disclaimer supernovaChain.unstake carries: the contract
 * decides what is actually released. It is not a settlement, not a quote in the
 * financial sense, and not a promise. It moves every second, which is why every
 * figure carries the `quotedAtMs` it was taken at.
 *
 * UNREADABLE IS NOT ZERO. A lot we can't rate (a malformed record, a missing
 * lock duration) comes back `readable: false` with null figures, never zeros.
 * The table renders em dashes for it and does not offer an Unstake button — we
 * won't put a member in front of a signature over a position we can't describe.
 */

/* ── Tier naming ────────────────────────────────────────────────────────────
   The tier table in config carries multipliers and day thresholds but no member
   -facing names, and the unstake modal has to say which tier it re-rated to.
   Names are built from the threshold itself so they cannot disagree with it. */

/** "30-day" · "180-day" · "1-year" · "3-year". */
export function tierName(days) {
  return days >= 365 && days % 365 === 0 ? `${days / 365}-year` : `${days}-day`;
}

/**
 * Name the tier `exitQuote` re-rated a lot to.
 *
 * It is a LOOKUP, not a re-derivation: the multiplier is the one the model
 * already chose, and the whole-days check is what keeps a lot that served less
 * than the 30-day minimum from being labelled "the 30-day tier" — that lot rates
 * at the 1.0× base because it completed no tier at all, and the two cases share
 * a multiplier but mean very different things to the member.
 *
 * @returns {{minDays: number, multiplier: number, hundredths: number, name: string}|null}
 *          null ⇒ no tier was completed (served under the 30-day floor).
 */
export function servedTierFor(servedDays, servedTierMultiplier) {
  const whole = Math.floor(servedDays);
  const tier = MULTIPLIER_TIERS.find(
    (t) => t.multiplier === servedTierMultiplier && whole >= t.minDays
  );
  return tier ? { ...tier, name: tierName(tier.minDays) } : null;
}

/* ── Rows ───────────────────────────────────────────────────────────────── */

/**
 * @typedef {Object} StakeRow
 * @property {boolean} readable    false ⇒ we could not rate this lot; figures null
 * @property {string|null} lotId
 * @property {number|null} tokenId
 * @property {string|null} tokenName
 * @property {number|null} qty
 * @property {number|null} lockDays
 * @property {string|null} lockLabel      "90 days" / "3 years"
 * @property {string|null} lockName       "90-day" / "3-year", for "…lock" copy
 * @property {string|null} lockChipLabel  "90d" / "3y"
 * @property {number|null} multiplier     the CONTRACTED multiplier
 * @property {number|null} stakedAtMs
 * @property {number|null} unlockAtMs
 * @property {boolean|null} matured
 * @property {number|null} servedDays
 * @property {number|null} earnedXp       accrued so far at the contracted rate
 * @property {number|null} xpAtMaturity   model ceiling for the full term
 * @property {number} quotedAtMs
 */

/**
 * One lot → one table row.
 *
 * `earnedXp` is what the member has IF THEY HOLD TO TERM. It is deliberately not
 * the early-exit figure: the row shows what time has earned, and the modal shows
 * what leaving right now would actually cost. Collapsing the two would either
 * understate a matured lot or overstate an early exit.
 *
 * @param {Object} lot  a StakeLotRecord from supernovaChain.getStaked
 * @param {number|Date} [nowMs]
 * @returns {StakeRow}
 */
export function buildStakeRow(lot, nowMs = Date.now()) {
  const now = nowMs instanceof Date ? nowMs.getTime() : nowMs;
  const tokenId = lot && typeof lot.tokenId === "number" ? lot.tokenId : null;
  const token = SUPERNOVA_TOKENS.find((t) => t.id === tokenId) || null;

  const unreadable = {
    readable: false,
    lotId: lot && lot.lotId ? String(lot.lotId) : null,
    tokenId,
    tokenName: token ? token.name : null,
    qty: null,
    lockDays: null,
    lockLabel: null,
    lockName: null,
    lockChipLabel: null,
    multiplier: null,
    stakedAtMs: null,
    unlockAtMs: null,
    matured: null,
    servedDays: null,
    earnedXp: null,
    xpAtMaturity: null,
    quotedAtMs: now,
  };

  if (!lot || !token || !lot.lotId) return unreadable;

  const fields = {
    qty: lot.qty,
    lockDays: lot.lockDays,
    stakedAtMs: lot.stakedAtMs,
    unlockAtMs: lot.unlockAtMs,
  };

  try {
    /* normalizeLot (inside every one of these) throws on anything it can't rate.
       We let it: an unrateable lot renders as unreadable, never as 0 XP. */
    const preset = presetForDays(lot.lockDays);
    return {
      readable: true,
      lotId: String(lot.lotId),
      tokenId,
      tokenName: token.name,
      qty: lot.qty,
      lockDays: lot.lockDays,
      lockLabel: preset ? preset.label : `${lot.lockDays} days`,
      lockName: tierName(lot.lockDays),
      lockChipLabel: lockChipLabel(lot.lockDays),
      /* The lot's CONTRACTED multiplier — the rate it accrues at while it is
         held, straight off the config's tier table for the lock it bought. An
         early exit re-rates it downward; see buildExitQuote. */
      multiplier: multiplierForDays(lot.lockDays),
      stakedAtMs: lot.stakedAtMs,
      unlockAtMs: lot.unlockAtMs,
      matured: isMatured(fields, now),
      servedDays: daysServed(fields, now),
      earnedXp: roundXp(earnedXP(fields, now), 6),
      xpAtMaturity: roundXp(xpAtMaturity(fields), 6),
      quotedAtMs: now,
    };
  } catch (e) {
    return unreadable;
  }
}

/**
 * @param {Object|null} staked  a getStaked() result
 * @param {number|Date} [nowMs]
 * @returns {StakeRow[]}  matured lots first, then soonest to mature — the order
 *          a member acts in, since a matured lot is the one they can exit clean.
 */
export function buildStakeRows(staked, nowMs = Date.now()) {
  const now = nowMs instanceof Date ? nowMs.getTime() : nowMs;
  const rows = (staked?.lots || []).map((lot) => buildStakeRow(lot, now));
  return rows.sort((a, b) => {
    if (a.matured !== b.matured) return a.matured ? -1 : 1;
    return (a.unlockAtMs ?? Infinity) - (b.unlockAtMs ?? Infinity);
  });
}

/* ── The exit quote ─────────────────────────────────────────────────────── */

/**
 * @typedef {Object} ExitQuoteView
 * @property {StakeRow} row
 * @property {boolean} valid       false ⇒ the lot couldn't be rated
 * @property {boolean} matured
 * @property {number} keptXp       the hero number: what this exit walks away with
 * @property {number} earnedAtChosenXp  accrued at the CONTRACTED multiplier
 * @property {number} chosenMultiplier
 * @property {number} reratedXp    re-rated to the tier actually served
 * @property {Object|null} servedTier  {name, multiplier} — null ⇒ none completed
 * @property {number} servedTierMultiplier
 * @property {number} penaltyPct   decay penalty, 0–75
 * @property {number} penaltyXp
 * @property {number} penaltyEndsAtMs  the date the penalty reaches zero
 * @property {number} unearnedForfeitedXp  never accrued, so never lost twice
 * @property {number} servedDays
 * @property {number} daysRemaining
 * @property {number} quotedAtMs
 */

/**
 * What leaving THIS lot right now would pay, line by line.
 *
 * At or after maturity there is nothing to give up: kept === earned === the
 * lot's maturity figure, and the modal says "keep all of it".
 *
 * Before maturity the three stacked losses of the model apply, and each gets its
 * own line so the arithmetic is checkable rather than a single shrunken number:
 *
 *   1. accrued at the lock rate the member chose          (struck through)
 *   2. re-rated to the longest tier they actually served  (the real base)
 *   3. minus the decay penalty, which shrinks to zero at the unlock date
 *   + the unearned remainder, which was never accrued in the first place and is
 *     shown so nobody reads the re-rate as "where the rest of my XP went".
 *
 * @param {Object} lot  a StakeLotRecord
 * @param {number|Date} [nowMs]
 * @returns {ExitQuoteView}
 */
export function buildExitQuote(lot, nowMs = Date.now()) {
  const now = nowMs instanceof Date ? nowMs.getTime() : nowMs;
  const row = buildStakeRow(lot, now);

  if (!row.readable) {
    return { row, valid: false, matured: false, quotedAtMs: now };
  }

  const fields = {
    qty: row.qty,
    lockDays: row.lockDays,
    stakedAtMs: row.stakedAtMs,
    unlockAtMs: row.unlockAtMs,
  };
  const q = exitQuote(fields, now);
  const servedTier = q.matured
    ? null
    : servedTierFor(q.servedDays, q.servedTierMultiplier);

  return {
    row,
    valid: true,
    matured: q.matured,
    keptXp: roundXp(q.kept, 6),
    earnedAtChosenXp: roundXp(q.earned, 6),
    chosenMultiplier: row.multiplier,
    reratedXp: roundXp(q.rerated, 6),
    servedTier,
    servedTierMultiplier: q.servedTierMultiplier,
    penaltyPct: q.penaltyPct,
    penaltyXp: roundXp(q.penaltyXp, 6),
    /* The decay is linear in days remaining, so it hits zero exactly at the
       unlock date — the one number that makes "it shrinks" concrete. */
    penaltyEndsAtMs: row.unlockAtMs,
    /* xpAtMaturity − earned. Never accrued, so it is NOT part of the penalty,
       and presenting it as one would double-count the loss. */
    unearnedForfeitedXp: roundXp(Math.max(0, row.xpAtMaturity - q.earned), 6),
    xpAtMaturity: row.xpAtMaturity,
    servedDays: q.servedDays,
    daysRemaining: Math.max(0, row.lockDays - q.servedDays),
    quotedAtMs: now,
  };
}

export default buildStakeRows;
